import React, { PureComponent } from 'react';
import { McdmApprovalState, RegulationProposalAction } from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import { Checkbox } from 'antd';
import TimeUtils from 'app/common/util/TimeUtils';
import McdmMeasureTable, {
    MCDM_MEASURE_SELECTION_COLUMN,
    McdmMeasureColumns,
} from 'app/components/table/McdmMeasureTable';
import { MULTIPLE_SELECTION, MultipleSelection, SortMode } from 'app/common/table/ManagedTable';
import { SelectionLabel } from 'app/components/common/SelectionLabel';
import { TableUtils } from 'app/common/util/TableUtils';
import memoizeOne from 'memoize-one';
import { Filter } from 'app/common/table/Filter';
import Utils from 'app/common/util/Utils';
import {
    MeasureProposalActionFilter,
    MeasureProposalActiveOnlyFilter,
    MeasureProposalFilterSettings,
    MeasureProposalTvFilter,
} from 'app/components/dashboard/detailed/measures/MeasureProposalFilter';
import { TextFilterInput } from 'app/common/input/TextFilterInput';
import { Highlighter } from 'app/common/table/Highlighter';
import MeasureUtils from 'app/common/util/MeasureUtils';
import {
    OpenMcdmMeasure,
    PartialDashboardDataUpdateEvent,
    UpdateMcdmMeasureProposals,
} from 'app/common/eventbus/Events';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import { MonitoredMcdmMeasure } from 'app/model/SOL49';
import AppContext from 'app/common/context/AppContext';
import './McdmMeasurePanel.less';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { EntityType } from '../../../model/client/SOL49';

interface McdmMeasurePanelProps extends ModalPanelProps {
    proposals: Array<MonitoredMcdmMeasure>;
    filterSettings: MeasureProposalFilterSettings;
}

interface McdmMeasurePanelState {
    proposals: Array<MonitoredMcdmMeasure>;
    selected: Array<MonitoredMcdmMeasure>;
    filterSettings: MeasureProposalFilterSettings;
}

const MONITORED_PROPOSALS_HIGHLIGHTER: Highlighter<MonitoredMcdmMeasure> = {
    highlight(elem: MonitoredMcdmMeasure): string {
        const nmocApprovalState = MeasureUtils.getNMOCApprovalState(elem.mcdmMeasureTopic);
        // We can't completely rely on the MCDMApprovalState, because it doesn't get updated to "OK"/"REJECTED",
        // when we accept/reject the proposal in NMVP, thus we check the mcdm state which is more reliable
        if (nmocApprovalState === McdmApprovalState.UNKNOWN) {
            return 'highlight';
        } else if (MeasureUtils.isMcdmStateActive(elem.mcdmMeasureTopic.state)) {
            return '';
        } else {
            return 'inactive';
        }
    },
};

const sortMonitoredProposals = (a: MonitoredMcdmMeasure, b: MonitoredMcdmMeasure) =>
    TimeUtils.compare(a.measure.applicability.from, b.measure.applicability.from);

export class McdmMeasurePanel extends PureComponent<McdmMeasurePanelProps, McdmMeasurePanelState> {
    private MCDM_MEASURES_COLUMNS = [
        MCDM_MEASURE_SELECTION_COLUMN,
        McdmMeasureColumns.ID,
        McdmMeasureColumns.TV,
        McdmMeasureColumns.TYPE,
        McdmMeasureColumns.REGULATION_EOC,
        McdmMeasureColumns.START_TIME,
        McdmMeasureColumns.END_TIME,
        McdmMeasureColumns.CAPACITY,
        McdmMeasureColumns.CONSTRAINTS,
        McdmMeasureColumns.REASON,
        McdmMeasureColumns.ACTION,
        McdmMeasureColumns.STATUS(this.props.onClose),
    ];

    constructor(props) {
        super(props);
        this.state = {
            proposals: this.props.proposals,
            selected: [],
            filterSettings: this.props.filterSettings,
        };
    }

    private callBack = ({ proposals }: UpdateMcdmMeasureProposals) => this.setState({ proposals });

    componentDidMount() {
        AppContext.eventBus.register(UpdateMcdmMeasureProposals.NAME, this.callBack);
    }

    componentWillUnmount() {
        // Prevents further updates when modal is closed
        AppContext.eventBus.unregister(UpdateMcdmMeasureProposals.NAME, this.callBack);
    }

    private getSelectionLabelMenuItems = (): ItemType[] => [
        {
            key: 'open-solution',
            onClick: () => {
                MeasureUtils.acknowledgeMcdmMeasures(this.state.selected).then(mcdmMeasures => {
                    this.props.onClose();
                    OpenMcdmMeasure.fireEvent(mcdmMeasures);
                    PartialDashboardDataUpdateEvent.fireEvent(EntityType.MEASURE_PROPOSAL);
                });
            },
            label: 'Find solution',
        },
    ];

    private getFilter = memoizeOne(
        (monitoredMcdmMeasures: MonitoredMcdmMeasure[], settings: MeasureProposalFilterSettings) => {
            const { actions, tvFilter, activeOnly } = settings;
            const filters: Array<Filter<MonitoredMcdmMeasure>> = [
                actions ? new MeasureProposalActionFilter(actions) : undefined,
                tvFilter ? new MeasureProposalTvFilter(tvFilter) : undefined,
                activeOnly ? new MeasureProposalActiveOnlyFilter(activeOnly) : undefined,
            ]
                .filter(filter => Utils.isExists(filter))
                .map(filter => filter.filter());
            return {
                keep(monitoredMcdmMeasure: MonitoredMcdmMeasure) {
                    return filters.every(filter => filter.keep(monitoredMcdmMeasure));
                },
            };
        },
    );

    handleSelectionChange = (newSelection: MonitoredMcdmMeasure[]) => this.setState({ selected: newSelection });

    handleTextFilterChange = newFilter =>
        this.setState({ filterSettings: { ...this.state.filterSettings, tvFilter: newFilter } });

    selection = memoizeOne(
        (selectedProposals: MonitoredMcdmMeasure[]): MultipleSelection<MonitoredMcdmMeasure> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItems: selectedProposals,
        }),
    );

    filteredProposals = memoizeOne(
        (monitoredMcdmMeasures: MonitoredMcdmMeasure[], filter: MeasureProposalFilterSettings) =>
            TableUtils.getFilteredData(monitoredMcdmMeasures, this.getFilter(monitoredMcdmMeasures, filter)),
    );

    onRegActionsChange = checkedValues => {
        this.setState({
            filterSettings: {
                ...this.state.filterSettings,
                actions: checkedValues.map(v => RegulationProposalAction[v]),
            },
        });
    };

    onActiveOnlyChange = (event: CheckboxChangeEvent) => {
        this.setState({
            filterSettings: {
                ...this.state.filterSettings,
                activeOnly: event.target.checked,
            },
        });
    };

    render() {
        const proposals = this.filteredProposals(this.state.proposals, this.state.filterSettings)?.sort(
            sortMonitoredProposals,
        );
        const selectedProposals = this.state.selected.length;
        const height = `${10 + proposals.length * 3}em`;
        const { actions, activeOnly } = this.state.filterSettings;
        return (
            <Flex
                direction="vertical"
                className="regulation-proposals regulation-proposals-table overflow"
                style={{ height: height }}>
                <Flex direction="horizontal" className="detailed-subpanel-header" size="5%">
                    <Flex size="95%">
                        <Flex className="detailed-subpanel-header-left">
                            <SelectionLabel
                                visible={selectedProposals > 0}
                                title={`${selectedProposals} proposal${selectedProposals > 1 ? 's' : ''} selected`}
                                subTitle={`${proposals?.length || 0} in total`}
                                onSelectAll={() => this.setState({ selected: proposals })}
                                onClearSelection={() => this.setState({ selected: [] })}
                                style={{ marginTop: 0, marginBottom: 0 }}
                                extraMenuItems={this.getSelectionLabelMenuItems()}>
                                <div style={{ height: '1em', alignSelf: 'center' }}>
                                    <span className="detailed-subpanel-title">PROPOSALS</span>
                                    <span className="detailed-subpanel-title-info">{proposals?.length || 0}</span>
                                </div>
                            </SelectionLabel>
                        </Flex>
                        <Flex className="detailed-subpanel-header-right">
                            <Checkbox.Group
                                options={[
                                    RegulationProposalAction.CREATION,
                                    RegulationProposalAction.UPDATE,
                                    RegulationProposalAction.CANCELLATION,
                                ]}
                                value={actions}
                                onChange={this.onRegActionsChange}
                            />
                            <Checkbox checked={activeOnly} onChange={this.onActiveOnlyChange}>
                                ACTIVE
                            </Checkbox>
                            <TextFilterInput
                                value={this.state.filterSettings.tvFilter}
                                onTextFilterChange={this.handleTextFilterChange}
                            />
                        </Flex>
                    </Flex>
                    <Flex size="5%" />
                </Flex>
                <Flex className="regulation-proposals-table">
                    <McdmMeasureTable
                        highlighter={MONITORED_PROPOSALS_HIGHLIGHTER}
                        data={proposals}
                        columns={this.MCDM_MEASURES_COLUMNS}
                        selection={this.selection(this.state.selected)}
                        sortMode={SortMode.INTERNAL}
                        columnResizeDisabled={true}
                        webBehaviour={true}
                        estimatedRowHeight={55}
                    />
                </Flex>
            </Flex>
        );
    }
}
